import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import {
  Card,
  Segmented,
  Switch,
  Table,
  Skeleton,
  Tag,
  Tooltip,
  DatePicker,
  Collapse,
  Typography,
  Row,
  Col,
  Dropdown,
  Button,
  message,
} from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  ZAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip as RTooltip,
} from "recharts";
import { mainUrl } from "../helpers/constants";
import { useThemeTokens, Tokens } from "../hooks/useThemeTokens";
import { ScopePicker, Scope } from "../components/stats/ScopePicker";

// Экспортируются для унаследованного components/stats/citySelect.tsx —
// сам компонент больше не используется на этой странице, но его файл компилится.
export interface FormData {
  companyId?: string;
  startDate: string;
  endDate: string;
  url?: string;
  cityId?: string;
  zip?: string;
  countyId?: string;
  stateId?: string;
  name?: string;
}
export const inputStyle = {
  height: 38,
  borderColor: "#d9d9d9",
  borderWidth: 1,
  borderStyle: "solid",
  borderRadius: 6,
  paddingLeft: 10,
};

const { RangePicker } = DatePicker;
const { Text } = Typography;

// ---------- types ----------
interface Daily {
  date: string;
  totalClicks: number;
  totalImpressions: number;
  averageCtr: number;
  averagePosition: number;
}
interface LB {
  totals: { impressions: number; clicks: number; ctr: number; position: number };
  topQueries: any[];
  topSites: any[];
  topCities: any[];
  opportunities: any[];
  positionBuckets: { bucket: string; impressions: number }[];
  movers: { gainers: any[]; losers: any[] };
  coverage: {
    sitesTotal: number;
    sitesWithImpressions: number;
    sitesInStrikingDistance: number;
    sitesInvisible: number;
    sitesErrorFlagged: number;
  };
}

// ---------- helpers ----------
const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const nf = new Intl.NumberFormat("en");
const fInt = (n?: number) => (n == null ? "—" : nf.format(Math.round(n)));
const fCompact = (n?: number) => (n == null ? "—" : compact.format(n));
const fPos = (n?: number) => (n == null || !isFinite(n) ? "—" : n.toFixed(1));
const fPct = (frac?: number) =>
  frac == null || !isFinite(frac) ? "—" : `${(frac * 100).toFixed(2)}%`;

const sum = (a: Daily[], k: keyof Daily) =>
  a.reduce((s, d) => s + (Number(d[k]) || 0), 0);
const wPos = (a: Daily[]) => {
  const imp = sum(a, "totalImpressions");
  if (!imp) return 0;
  return a.reduce((s, d) => s + (d.averagePosition || 0) * (d.totalImpressions || 0), 0) / imp;
};

const auth = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

type Preset = "7d" | "28d" | "90d" | "custom";
const presetDays: Record<Exclude<Preset, "custom">, number> = { "7d": 7, "28d": 28, "90d": 90 };

// ---------- small presentational pieces ----------
function DeltaChip({
  cur,
  prev,
  invert = false,
  tokens,
  suffix = "",
}: {
  cur: number;
  prev?: number;
  invert?: boolean;
  tokens: Tokens;
  suffix?: string;
}) {
  if (prev == null) return null;
  if (prev === 0 && cur === 0)
    return <span style={{ color: tokens.muted, fontSize: 12 }}>no change</span>;
  const pct = prev === 0 ? 1 : (cur - prev) / prev;
  const improved = invert ? cur < prev : cur > prev;
  const flat = Math.abs(cur - prev) < 1e-9;
  const color = flat ? tokens.muted : improved ? tokens.good : tokens.bad;
  const arrow = flat ? "→" : improved ? "▲" : "▼";
  return (
    <span style={{ color, fontSize: 12, fontWeight: 600 }}>
      {arrow} {prev === 0 ? "new" : `${Math.abs(pct * 100).toFixed(0)}%`}
      {suffix}
    </span>
  );
}

function StatTile({
  label,
  value,
  color,
  spark,
  delta,
  tokens,
  faded,
  hint,
}: {
  label: string;
  value: string;
  color: string;
  spark: { v: number }[];
  delta?: React.ReactNode;
  tokens: Tokens;
  faded?: boolean;
  hint?: string;
}) {
  return (
    <Card size="small" style={{ background: tokens.card, borderColor: tokens.border }} bodyStyle={{ padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: tokens.ink2, fontSize: 12, fontWeight: 600, letterSpacing: 0.2 }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: color, marginRight: 7 }} />
          {label}
        </span>
        {hint && (
          <Tooltip title={hint}>
            <span style={{ color: tokens.muted, fontSize: 11, cursor: "help" }}>ⓘ</span>
          </Tooltip>
        )}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          marginTop: 6,
          color: faded ? tokens.muted : tokens.ink,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 4, height: 34 }}>
        <div style={{ minWidth: 60 }}>{delta}</div>
        <div style={{ width: 96, height: 30 }}>
          {spark.length > 1 && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spark} margin={{ top: 3, bottom: 0, left: 0, right: 0 }}>
                <defs>
                  <linearGradient id={`sp-${label}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#sp-${label})`} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Card>
  );
}

function SectionCard({
  title,
  extra,
  tokens,
  children,
  sub,
}: {
  title: string;
  extra?: React.ReactNode;
  tokens: Tokens;
  children: React.ReactNode;
  sub?: string;
}) {
  return (
    <Card
      size="small"
      style={{ background: tokens.card, borderColor: tokens.border, height: "100%" }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sub ? 2 : 12, gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: tokens.ink }}>{title}</span>
        {extra}
      </div>
      {sub && <div style={{ color: tokens.muted, fontSize: 12, marginBottom: 12 }}>{sub}</div>}
      {children}
    </Card>
  );
}

// inline magnitude bar for table cells
function bar(value: number, max: number, color: string, ink: string) {
  const w = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 40, height: 8, borderRadius: 3, background: `${color}22`, overflow: "hidden" }}>
        <div style={{ width: `${w}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontVariantNumeric: "tabular-nums", color: ink, minWidth: 48, textAlign: "right" }}>
        {fInt(value)}
      </span>
    </div>
  );
}

function EmptyNote({ text, tokens, height = 120 }: { text: string; tokens: Tokens; height?: number }) {
  return (
    <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: tokens.muted, fontSize: 13, padding: 12 }}>
      {text}
    </div>
  );
}

// themed recharts tooltip
const makeTooltip = (tokens: Tokens, rows: (p: any) => { label: string; val: string; color?: string }[]) =>
  ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div style={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: 8, padding: "8px 10px", boxShadow: "0 4px 16px rgba(0,0,0,.18)" }}>
        {label != null && <div style={{ color: tokens.ink2, fontSize: 11, marginBottom: 4 }}>{label}</div>}
        {rows(p).map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 10, justifyContent: "space-between", fontSize: 12, color: tokens.ink }}>
            <span style={{ color: r.color || tokens.ink2 }}>{r.label}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{r.val}</span>
          </div>
        ))}
      </div>
    );
  };

const posColor = (p: number, tokens: Tokens) =>
  p <= 10 ? tokens.good : p <= 20 ? tokens.opp : p <= 30 ? tokens.ctr : tokens.muted;

// ---------- main ----------
export function Clicks() {
  const tokens = useThemeTokens();
  const [preset, setPreset] = useState<Preset>("28d");
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(28, "day"), dayjs()]);
  const [compare, setCompare] = useState(true);
  const [scope, setScope] = useState<Scope>({ type: "portfolio" });
  const [trendMetric, setTrendMetric] = useState<"impressions" | "position" | "ctr" | "clicks">("impressions");
  const [leaderTab, setLeaderTab] = useState<"sites" | "cities">("sites");

  const [agg, setAgg] = useState<Daily[] | null>(null);
  const [aggPrev, setAggPrev] = useState<Daily[] | null>(null);
  const [lb, setLb] = useState<LB | null>(null);
  const [conversions, setConversions] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [writing, setWriting] = useState<Record<string, boolean>>({});
  const reqId = useRef(0);

  // написать статью/отзыв под конкретный запрос (кнопка в opportunities)
  const writeFor = async (row: any, type: "article" | "review") => {
    const key = `${row.query}-${row.siteId}`;
    setWriting((w) => ({ ...w, [key]: true }));
    try {
      await axios.post(
        `${mainUrl}api/v1/blog/write-for-query`,
        { siteId: row.siteId, query: row.query, type },
        auth()
      );
      message.success(
        `${type === "review" ? "Review" : "Article"} for “${row.query}” started on ${row.domain}. Publishes in ~10–15 min.`,
        5
      );
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to start generation");
    } finally {
      setWriting((w) => ({ ...w, [key]: false }));
    }
  };

  const range = useMemo(() => {
    if (preset === "custom") return { start: customRange[0], end: customRange[1] };
    const end = dayjs();
    return { start: end.subtract(presetDays[preset] - 1, "day"), end };
  }, [preset, customRange]);

  const prevWindow = useMemo(() => {
    const len = range.end.diff(range.start, "day") + 1;
    const pEnd = range.start.subtract(1, "day");
    return { start: pEnd.subtract(len - 1, "day"), end: pEnd };
  }, [range]);

  const scopeBody = useMemo(
    () => (scope.paramKey && scope.value ? { [scope.paramKey]: scope.value } : {}),
    [scope]
  );

  const fmt = (d: Dayjs) => d.format("YYYY-MM-DD");

  useEffect(() => {
    const id = ++reqId.current;
    const s = fmt(range.start), e = fmt(range.end);
    const ps = fmt(prevWindow.start), pe = fmt(prevWindow.end);
    setLoading(true);
    setErr(null);

    const aggReq = axios.post(`${mainUrl}api/v1/domain/agregated-stats`, { startDate: s, endDate: e, ...scopeBody }, auth());
    const prevReq = compare
      ? axios.post(`${mainUrl}api/v1/domain/agregated-stats`, { startDate: ps, endDate: pe, ...scopeBody }, auth())
      : Promise.resolve({ data: { stats: [] } });
    const lbReq = axios.post(
      `${mainUrl}api/v1/domain/stats-leaderboards`,
      { startDate: s, endDate: e, ...(compare ? { compareStartDate: ps, compareEndDate: pe } : {}), limit: 15, ...scopeBody },
      auth()
    );
    const convReq = axios.post(`${mainUrl}api/v1/domain/all-search-analytics`, { startDate: s, endDate: e, onlyClicks: true }, auth());

    Promise.all([aggReq, prevReq, lbReq, convReq])
      .then(([a, p, l, c]) => {
        if (id !== reqId.current) return;
        const sortD = (x: any[]) =>
          (x || []).map((d) => ({ ...d, date: d.date })).sort((m, n) => +new Date(m.date) - +new Date(n.date));
        setAgg(sortD(a.data.stats));
        setAggPrev(compare ? sortD(p.data.stats) : null);
        setLb(l.data);
        setConversions((c.data.stats || []).sort((m: any, n: any) => +new Date(n.createdAt) - +new Date(m.createdAt)));
      })
      .catch((e2) => {
        if (id !== reqId.current) return;
        setErr(e2?.response?.data?.message || "Failed to load stats");
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customRange, compare, scope]);

  // ---- KPI aggregates ----
  const cur = agg || [];
  const prv = aggPrev || [];
  const kpi = {
    impressions: sum(cur, "totalImpressions"),
    clicks: sum(cur, "totalClicks"),
    position: wPos(cur),
    ctr: sum(cur, "totalImpressions") ? sum(cur, "totalClicks") / sum(cur, "totalImpressions") : 0,
  };
  const kpiPrev = compare && prv.length
    ? {
        impressions: sum(prv, "totalImpressions"),
        clicks: sum(prv, "totalClicks"),
        position: wPos(prv),
        ctr: sum(prv, "totalImpressions") ? sum(prv, "totalClicks") / sum(prv, "totalImpressions") : 0,
      }
    : undefined;

  const spark = (k: keyof Daily) => cur.map((d) => ({ v: Number(d[k]) || 0 }));

  // ---- trend data ----
  const metricCfg = {
    impressions: { label: "Impressions", color: tokens.impr, key: "totalImpressions" as const, kind: "area" as const },
    position: { label: "Avg position", color: tokens.pos, key: "averagePosition" as const, kind: "area" as const, reversed: true },
    ctr: { label: "CTR", color: tokens.ctr, key: "averageCtr" as const, kind: "area" as const, pct: true },
    clicks: { label: "Clicks", color: tokens.clicks, key: "totalClicks" as const, kind: "bar" as const },
  };
  const mc = metricCfg[trendMetric];
  const trendData = cur.map((d) => ({
    date: dayjs(d.date).format("MMM D"),
    value: (mc as any).pct ? (Number(d[mc.key]) || 0) * 100 : Number(d[mc.key]) || 0,
  }));
  const clicksAllZero = trendMetric === "clicks" && !trendData.some((d) => d.value > 0);

  const chartAxis = { stroke: tokens.axis, fontSize: 11, tick: { fill: tokens.muted } as any };
  const gridEl = <CartesianGrid stroke={tokens.grid} strokeDasharray="3 3" vertical={false} />;

  // ---- leaderboard rows ----
  const leaderRows = leaderTab === "sites" ? lb?.topSites || [] : lb?.topCities || [];
  const leaderMax = Math.max(1, ...leaderRows.map((r) => r.impressions));

  const oppMax = Math.max(1, ...(lb?.opportunities || []).map((o) => o.impressions));
  const oppScoreMax = Math.max(1, ...(lb?.opportunities || []).map((o) => o.score));
  const tqMax = Math.max(1, ...(lb?.topQueries || []).map((q) => q.impressions));

  const buckets = lb?.positionBuckets || [];
  const bucketTotal = buckets.reduce((s, b) => s + b.impressions, 0);

  const moverMax = Math.max(1, ...[...(lb?.movers.gainers || []), ...(lb?.movers.losers || [])].map((m) => Math.abs(m.deltaImpr)));

  const page = { padding: 16, background: tokens.pagePlane, minHeight: "100%" };

  return (
    <div style={page}>
      {/* header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <span style={{ fontSize: 22, fontWeight: 800, color: tokens.ink }}>Search Performance</span>
          <span style={{ color: tokens.muted, marginLeft: 10, fontSize: 13 }}>
            Google Search visibility across the site portfolio
          </span>
        </div>
        {scope.type !== "portfolio" && scope.value && (
          <Tag closable color="blue" onClose={() => setScope({ type: "portfolio" })}>
            Portfolio › {scope.label || scope.value}
          </Tag>
        )}
      </div>

      {/* filter bar */}
      <Card size="small" style={{ background: tokens.card, borderColor: tokens.border, marginBottom: 14, position: "sticky", top: 0, zIndex: 5 }} bodyStyle={{ padding: 10 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <Segmented
            value={preset}
            onChange={(v) => setPreset(v as Preset)}
            options={[
              { label: "7d", value: "7d" },
              { label: "28d", value: "28d" },
              { label: "90d", value: "90d" },
              { label: "Custom", value: "custom" },
            ]}
          />
          {preset === "custom" && (
            <RangePicker
              value={customRange}
              onChange={(v) => v && v[0] && v[1] && setCustomRange([v[0], v[1]])}
              allowClear={false}
            />
          )}
          <ScopePicker value={scope} onChange={setScope} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: tokens.ink2 }}>
            <Switch size="small" checked={compare} onChange={setCompare} />
            <span style={{ fontSize: 13 }}>Compare to previous period</span>
          </div>
        </div>
      </Card>

      {err && (
        <Card size="small" style={{ marginBottom: 14, borderColor: tokens.bad }}>
          <Text style={{ color: tokens.bad }}>{err}</Text>
        </Card>
      )}

      {/* KPI row */}
      <Row gutter={[14, 14]}>
        {(
          [
            { key: "impressions", label: "Impressions", color: tokens.impr, val: fCompact(kpi.impressions), sk: "totalImpressions", invert: false },
            { key: "position", label: "Avg position", color: tokens.pos, val: fPos(kpi.position), sk: "averagePosition", invert: true, hint: "Lower is better. Impression-weighted across the range." },
            { key: "ctr", label: "CTR", color: tokens.ctr, val: fPct(kpi.ctr), sk: "averageCtr", invert: false },
            { key: "clicks", label: "Clicks", color: tokens.clicks, val: fInt(kpi.clicks), sk: "totalClicks", invert: false, faded: true, hint: "Early-stage portfolio — clicks are rare; watch impressions & position." },
          ] as const
        ).map((t) => (
          <Col xs={12} lg={6} key={t.key}>
            {loading && !agg ? (
              <Card size="small" style={{ background: tokens.card, borderColor: tokens.border }}><Skeleton active paragraph={{ rows: 1 }} /></Card>
            ) : (
              <StatTile
                label={t.label}
                value={t.val}
                color={t.color}
                faded={(t as any).faded}
                hint={(t as any).hint}
                spark={spark(t.sk as keyof Daily)}
                tokens={tokens}
                delta={
                  kpiPrev ? (
                    <DeltaChip cur={(kpi as any)[t.key]} prev={(kpiPrev as any)[t.key]} invert={t.invert} tokens={tokens} />
                  ) : undefined
                }
              />
            )}
          </Col>
        ))}
      </Row>

      {/* coverage strip */}
      <Card size="small" style={{ background: tokens.card, borderColor: tokens.border, marginTop: 14 }} bodyStyle={{ padding: "10px 16px" }}>
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Sites live", v: lb?.coverage.sitesTotal, c: tokens.ink2 },
            { label: "Getting impressions", v: lb?.coverage.sitesWithImpressions, c: tokens.impr },
            { label: "Within striking distance", v: lb?.coverage.sitesInStrikingDistance, c: tokens.opp },
            { label: "Still invisible", v: lb?.coverage.sitesInvisible, c: tokens.muted },
            { label: "Flagged (error)", v: lb?.coverage.sitesErrorFlagged, c: tokens.bad },
          ].map((s) => (
            <div key={s.label} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: s.c, fontVariantNumeric: "tabular-nums" }}>{s.v == null ? "—" : s.v}</span>
              <span style={{ fontSize: 12, color: tokens.muted }}>{s.label}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* primary trend */}
      <div style={{ marginTop: 14 }}>
        <SectionCard
          title="Trend"
          tokens={tokens}
          extra={
            <Segmented
              size="small"
              value={trendMetric}
              onChange={(v) => setTrendMetric(v as any)}
              options={[
                { label: "Impressions", value: "impressions" },
                { label: "Position", value: "position" },
                { label: "CTR", value: "ctr" },
                { label: "Clicks", value: "clicks" },
              ]}
            />
          }
        >
          {clicksAllZero ? (
            <EmptyNote text="No clicks recorded in this range — switch to Impressions or Position to see movement." tokens={tokens} height={260} />
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                {mc.kind === "bar" ? (
                  <BarChart data={trendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    {gridEl}
                    <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
                    <YAxis {...chartAxis} width={44} allowDecimals={false} />
                    <RTooltip content={makeTooltip(tokens, (p: any) => [{ label: mc.label, val: fInt(p.value), color: mc.color }])} cursor={{ fill: `${mc.color}14` }} />
                    <Bar dataKey="value" fill={mc.color} radius={[3, 3, 0, 0]} maxBarSize={26} isAnimationActive={false} />
                  </BarChart>
                ) : (
                  <AreaChart data={trendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={mc.color} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={mc.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    {gridEl}
                    <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
                    <YAxis
                      {...chartAxis}
                      width={44}
                      reversed={(mc as any).reversed}
                      domain={(mc as any).reversed ? [1, "dataMax"] : (mc as any).pct ? [0, "auto"] : [0, "auto"]}
                      tickFormatter={(v) => ((mc as any).pct ? `${v.toFixed(2)}%` : compact.format(v))}
                    />
                    {(mc as any).reversed && (
                      <>
                        <ReferenceLine y={10} stroke={tokens.good} strokeDasharray="4 4" label={{ value: "page 1", fill: tokens.muted, fontSize: 10, position: "insideTopRight" }} />
                        <ReferenceLine y={20} stroke={tokens.axis} strokeDasharray="4 4" label={{ value: "page 2", fill: tokens.muted, fontSize: 10, position: "insideTopRight" }} />
                      </>
                    )}
                    <RTooltip content={makeTooltip(tokens, (p: any) => [{ label: mc.label, val: (mc as any).pct ? `${p.value.toFixed(2)}%` : (mc as any).reversed ? p.value.toFixed(1) : fInt(p.value), color: mc.color }])} cursor={{ stroke: tokens.axis, strokeDasharray: "3 3" }} />
                    <Area type="monotone" dataKey="value" stroke={mc.color} strokeWidth={2} fill="url(#trendFill)" dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

      {/* opportunities */}
      <div style={{ marginTop: 14 }}>
        <SectionCard
          title="Striking-distance opportunities"
          sub="Queries already earning impressions on page 2–3 — closest to breaking into page 1. Your action list."
          tokens={tokens}
        >
          {loading && !lb ? (
            <Skeleton active />
          ) : !lb?.opportunities.length ? (
            <EmptyNote text="No page-2/3 keywords yet. As impressions grow, near-win queries will surface here — watch the trend above." tokens={tokens} height={140} />
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={14}>
                <Table
                  size="small"
                  rowKey={(r) => `${r.query}-${r.siteId}`}
                  dataSource={lb.opportunities}
                  pagination={false}
                  scroll={{ x: true }}
                  columns={[
                    { title: "Query", dataIndex: "query", ellipsis: true },
                    { title: "Site", dataIndex: "domain", ellipsis: true, width: 170, render: (d) => <span style={{ color: tokens.ink2 }}>{d}</span> },
                    { title: "Impr.", dataIndex: "impressions", width: 150, sorter: (a, b) => a.impressions - b.impressions, render: (v) => bar(v, oppMax, tokens.impr, tokens.ink) },
                    { title: "Pos.", dataIndex: "position", width: 70, align: "center" as const, sorter: (a, b) => a.position - b.position, render: (p) => <Tag color={undefined} style={{ background: `${posColor(p, tokens)}22`, color: posColor(p, tokens), border: "none", fontVariantNumeric: "tabular-nums" }}>{p.toFixed(1)}</Tag> },
                    { title: "Opportunity", dataIndex: "score", width: 130, defaultSortOrder: "descend" as const, sorter: (a, b) => a.score - b.score, render: (v) => bar(v, oppScoreMax, tokens.opp, tokens.ink) },
                    {
                      title: "Written",
                      key: "written",
                      width: 96,
                      align: "center" as const,
                      render: (_: any, row: any) => {
                        const fmt = (d: string) => dayjs(d).format("MMM D");
                        if (!row.articleWrittenAt && !row.reviewWrittenAt)
                          return <span style={{ color: tokens.ink2, opacity: 0.45 }}>—</span>;
                        return (
                          <span style={{ display: "inline-flex", gap: 4, justifyContent: "center" }}>
                            {row.articleWrittenAt && (
                              <Tooltip title={`Article written ${fmt(row.articleWrittenAt)}`}>
                                <Tag color="green" style={{ margin: 0, cursor: "default" }}>✍️</Tag>
                              </Tooltip>
                            )}
                            {row.reviewWrittenAt && (
                              <Tooltip title={`Review written ${fmt(row.reviewWrittenAt)}`}>
                                <Tag color="green" style={{ margin: 0, cursor: "default" }}>⭐</Tag>
                              </Tooltip>
                            )}
                          </span>
                        );
                      },
                    },
                    {
                      title: "Write",
                      key: "write",
                      width: 92,
                      align: "center" as const,
                      render: (_: any, row: any) => {
                        const k = `${row.query}-${row.siteId}`;
                        return (
                          <Dropdown
                            trigger={["click"]}
                            disabled={writing[k] || row.domain === "—"}
                            menu={{
                              items: [
                                { key: "article", label: row.articleWrittenAt ? "✍️ Write article (again)" : "✍️ Write article", onClick: () => writeFor(row, "article") },
                                { key: "review", label: row.reviewWrittenAt ? "⭐ Write review (again)" : "⭐ Write review", onClick: () => writeFor(row, "review") },
                              ],
                            }}
                          >
                            <Button size="small" type="primary" ghost loading={writing[k]}>
                              Write ▾
                            </Button>
                          </Dropdown>
                        );
                      },
                    },
                  ]}
                />
              </Col>
              <Col xs={24} xl={10}>
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <ScatterChart margin={{ top: 10, right: 16, bottom: 24, left: 4 }}>
                      {gridEl}
                      <ReferenceArea x1={8} x2={20} fill={tokens.opp} fillOpacity={0.06} />
                      <XAxis type="number" dataKey="position" name="Position" reversed domain={[1, 31]} {...chartAxis} label={{ value: "position (→ better)", fill: tokens.muted, fontSize: 11, position: "insideBottom", offset: -12 }} />
                      <YAxis type="number" dataKey="impressions" name="Impr" {...chartAxis} width={44} tickFormatter={(v) => compact.format(v)} />
                      <ZAxis type="number" dataKey="impressions" range={[40, 400]} />
                      <RTooltip content={makeTooltip(tokens, (p: any) => [{ label: p.query, val: "" }, { label: "Impressions", val: fInt(p.impressions), color: tokens.impr }, { label: "Position", val: p.position?.toFixed(1) }])} cursor={{ strokeDasharray: "3 3", stroke: tokens.axis }} />
                      <Scatter data={lb.opportunities} fill={tokens.opp} fillOpacity={0.7} isAnimationActive={false} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          )}
        </SectionCard>
      </div>

      {/* movers */}
      {compare && (
        <div style={{ marginTop: 14 }}>
          <Row gutter={[14, 14]}>
            {(["gainers", "losers"] as const).map((side) => {
              const rows = lb?.movers[side] || [];
              const isGain = side === "gainers";
              return (
                <Col xs={24} lg={12} key={side}>
                  <SectionCard title={isGain ? "Rising sites" : "Falling sites"} sub={`Biggest ${isGain ? "gain" : "drop"} in impressions vs previous period`} tokens={tokens}>
                    {loading && !lb ? <Skeleton active /> : !rows.length ? (
                      <EmptyNote text="No significant changes this period." tokens={tokens} height={100} />
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {rows.map((m) => (
                          <div key={m.siteId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ width: 190, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: tokens.ink2 }}>{m.domain}</span>
                            <div style={{ flex: 1, height: 10, background: `${(isGain ? tokens.good : tokens.bad)}18`, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${(Math.abs(m.deltaImpr) / moverMax) * 100}%`, height: "100%", background: isGain ? tokens.good : tokens.bad, borderRadius: 3 }} />
                            </div>
                            <span style={{ width: 96, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13, color: isGain ? tokens.good : tokens.bad, fontWeight: 600 }}>
                              {isGain ? "+" : "−"}{fCompact(Math.abs(m.deltaImpr))}
                              <span style={{ color: tokens.muted, fontWeight: 400, marginLeft: 4 }}>{isFinite(m.pct) ? `${Math.abs(m.pct * 100).toFixed(0)}%` : ""}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </SectionCard>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* leaderboard */}
      <div style={{ marginTop: 14 }}>
        <SectionCard
          title="Portfolio leaderboard"
          tokens={tokens}
          extra={<Segmented size="small" value={leaderTab} onChange={(v) => setLeaderTab(v as any)} options={[{ label: "Sites", value: "sites" }, { label: "Cities (locations)", value: "cities" }]} />}
        >
          {loading && !lb ? <Skeleton active /> : !leaderRows.length ? <EmptyNote text="No data in range." tokens={tokens} /> : (
            <Table
              size="small"
              rowKey={(r) => r.siteId || r.cityId}
              dataSource={leaderRows}
              pagination={false}
              scroll={{ x: true }}
              onRow={(r) =>
                leaderTab === "sites" && r.domain && r.domain !== "—"
                  ? { style: { cursor: "pointer" }, onClick: () => setScope({ type: "site", paramKey: "url", value: r.domain, label: r.domain }) }
                  : {}
              }
              columns={[
                leaderTab === "sites"
                  ? { title: "Site", dataIndex: "domain", ellipsis: true, render: (d) => <span style={{ color: tokens.impr }}>{d}</span> }
                  : { title: "City", dataIndex: "city", ellipsis: true, render: (c, r: any) => <span>{c}{r.state ? <span style={{ color: tokens.muted }}>, {r.state}</span> : null}</span> },
                { title: "Impressions", dataIndex: "impressions", width: 220, defaultSortOrder: "descend" as const, sorter: (a, b) => a.impressions - b.impressions, render: (v) => bar(v, leaderMax, tokens.impr, tokens.ink) },
                { title: "Avg pos.", dataIndex: "position", width: 90, align: "center" as const, sorter: (a, b) => a.position - b.position, render: (p) => <span style={{ color: posColor(p, tokens), fontVariantNumeric: "tabular-nums" }}>{fPos(p)}</span> },
                { title: "Clicks", dataIndex: "clicks", width: 80, align: "right" as const, render: (v) => <span style={{ color: v ? tokens.clicks : tokens.muted, fontVariantNumeric: "tabular-nums" }}>{fInt(v)}</span> },
              ]}
            />
          )}
        </SectionCard>
      </div>

      {/* top queries */}
      <div style={{ marginTop: 14 }}>
        <SectionCard title="Top queries" sub="Aggregated across the selected range, by impressions." tokens={tokens}>
          {loading && !lb ? <Skeleton active /> : !lb?.topQueries.length ? <EmptyNote text="No queries in range." tokens={tokens} /> : (
            <Table
              size="small"
              rowKey={(r) => r.query}
              dataSource={lb.topQueries}
              pagination={{ pageSize: 10, size: "small", hideOnSinglePage: true }}
              scroll={{ x: true }}
              columns={[
                { title: "Query", dataIndex: "query", ellipsis: true },
                { title: "Impressions", dataIndex: "impressions", width: 220, defaultSortOrder: "descend" as const, sorter: (a, b) => a.impressions - b.impressions, render: (v) => bar(v, tqMax, tokens.impr, tokens.ink) },
                { title: "Pos.", dataIndex: "position", width: 80, align: "center" as const, sorter: (a, b) => a.position - b.position, render: (p) => <span style={{ color: posColor(p, tokens), fontVariantNumeric: "tabular-nums" }}>{fPos(p)}</span> },
                { title: "CTR", dataIndex: "ctr", width: 80, align: "right" as const, render: (v) => <span style={{ color: tokens.muted, fontVariantNumeric: "tabular-nums" }}>{fPct(v)}</span> },
                { title: "Clicks", dataIndex: "clicks", width: 70, align: "right" as const, render: (v) => <span style={{ color: v ? tokens.clicks : tokens.muted, fontVariantNumeric: "tabular-nums" }}>{fInt(v)}</span> },
              ]}
            />
          )}
        </SectionCard>
      </div>

      {/* distribution + recent conversions */}
      <div style={{ marginTop: 14 }}>
        <Row gutter={[14, 14]}>
          <Col xs={24} lg={12}>
            <SectionCard title="Where your visibility sits" sub="Share of impressions by SERP page (current range)." tokens={tokens}>
              {loading && !lb ? <Skeleton active /> : !bucketTotal ? <EmptyNote text="No impressions in range." tokens={tokens} /> : (
                <div>
                  <div style={{ display: "flex", height: 34, borderRadius: 6, overflow: "hidden", gap: 2 }}>
                    {buckets.map((b, i) => {
                      const share = b.impressions / bucketTotal;
                      if (share <= 0) return null;
                      return (
                        <Tooltip key={b.bucket} title={`Page ${i + 1} (pos ${b.bucket}): ${fInt(b.impressions)} impr · ${(share * 100).toFixed(1)}%`}>
                          <div style={{ width: `${share * 100}%`, background: tokens.bucket[i], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", fontWeight: 600 }}>
                            {share >= 0.08 ? `${Math.round(share * 100)}%` : ""}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                    {buckets.map((b, i) => (
                      <span key={b.bucket} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: tokens.ink2 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: tokens.bucket[i] }} />
                        Page {i + 1} <span style={{ color: tokens.muted }}>(pos {b.bucket})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          </Col>
          <Col xs={24} lg={12}>
            <SectionCard title="Recent clicks" sub="The rare conversions — impressions that turned into a visit." tokens={tokens}>
              {conversions == null ? <Skeleton active /> : !conversions.length ? (
                <EmptyNote text="No clicks in this range yet. This fills in as rankings climb into page 1." tokens={tokens} height={140} />
              ) : (
                <div style={{ maxHeight: 260, overflow: "auto" }}>
                  {conversions.slice(0, 40).map((c, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: `1px solid ${tokens.border}` }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: tokens.ink, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.query}</div>
                        <div style={{ color: tokens.muted, fontSize: 11 }}>{c.url} · pos {Number(c.position).toFixed(1)}</div>
                      </div>
                      <Tag color="purple" style={{ height: 22 }}>{c.clicks} click{c.clicks > 1 ? "s" : ""}</Tag>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </Col>
        </Row>
      </div>

      {/* raw detail */}
      <div style={{ marginTop: 14 }}>
        <Collapse
          size="small"
          style={{ background: tokens.card, borderColor: tokens.border }}
          items={[
            {
              key: "raw",
              label: <span style={{ color: tokens.ink2 }}>Raw leaderboard data (debug)</span>,
              children: (
                <pre style={{ maxHeight: 360, overflow: "auto", fontSize: 11, color: tokens.ink2, margin: 0 }}>
                  {JSON.stringify(lb, null, 2)}
                </pre>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
